import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { FaChevronDown } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  setPatientDataBounds,
  getPatientDataBounds,
} from "../../services/patient/settings";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import Button from "../ui/button";
import Spinner from "../spinner";
import Reload from "../ui/reload-data";
// Validation schema for numeric inputs

const numericSchema = z
  .string()
  .optional()
  .refine((val) => {
    // Skip validation for empty values
    if (!val || val === "") return true;
    // Check if it's a valid number
    return !isNaN(Number(val));
  }, "Must be a valid number")
  .transform((val) => {
    // Convert to number if not empty
    if (val && val !== "") {
      return Number(val);
    }
    return val;
  })
  .refine((val) => {
    // Skip validation for empty values
    if (val === null || val === undefined || val === "") return true;
    // Check if it's positive
    return val >= 0;
  }, "Value must be positive");

const dataBoundsSchema = z.object({
  glucose_low: numericSchema,
  glucose_high: numericSchema,
  systolic_low: numericSchema,
  systolic_high: numericSchema,
  diastolic_low: numericSchema,
  diastolic_high: numericSchema,
  heart_rate_low: numericSchema,
  heart_rate_high: numericSchema,
  temperature_low: numericSchema,
  temperature_high: numericSchema,
  weight_low: numericSchema,
  weight_high: numericSchema,
  oxygen_low: numericSchema,
  oxygen_high: numericSchema,
});

const defaultSchema = {
  glucose_low: "",
  glucose_high: "",
  systolic_low: "",
  systolic_high: "",
  diastolic_low: "",
  diastolic_high: "",
  heart_rate_low: "",
  heart_rate_high: "",
  temperature_low: "",
  temperature_high: "",
  weight_low: "",
  weight_high: "",
  oxygen_low: "",
  oxygen_high: "",
};

const DataBounds = forwardRef(
  ({ isOpen, toggleSection, onSubmit, data_bounds, setFormData }, ref) => {
    const patientId = sessionStorage.getItem("_pt_id");

    const {
      register,
      handleSubmit,
      setValue,
      watch,
      getValues,
      trigger,
      formState: { errors },
    } = useForm({
      resolver: zodResolver(dataBoundsSchema),
      defaultValues: defaultSchema,
      mode: "onChange", // Enable validation on change
    });

    // Expose a method to get validated form data
    useImperativeHandle(ref, () => ({
      getValidatedData: async () => {
        const isValid = await trigger(); // Trigger validation
        if (isValid) {
          const formValues = getValues();

          // All-or-nothing rule: Check if any field has a value
          const hasAnyValue = Object.values(formValues).some(
            (val) => val !== "" && val !== null && val !== undefined
          );

          // Convert string values to numbers for all fields
          const convertedValues = {};
          Object.entries(formValues).forEach(([key, value]) => {
            // Only convert non-empty strings to numbers
            if (value !== "" && value !== null && value !== undefined) {
              convertedValues[key] = Number(value);
            } else {
              // Send null for empty values - backend will use organizational defaults
              convertedValues[key] = null;
            }
          });

          // All-or-nothing: If any value is entered, ALL must be entered
          if (hasAnyValue) {
            const allFilled = Object.values(formValues).every(
              (val) => val !== "" && val !== null && val !== undefined
            );
            if (!allFilled) {
              // Not all fields filled - return null to indicate validation error
              return null;
            }
          }

          return convertedValues;
        }
        return null; // Return null if validation fails
      },
    }));

    const onFormSubmit = (data) => {
      // This is just for local form submission if needed
    };

    useEffect(() => {
      if (data_bounds) {
        Object.entries(data_bounds)?.forEach(([key, value]) => {
          setValue(key, value?.toString() ?? "");
        });
      }
    }, [data_bounds]);

    // Remove the watch effect that was immediately setting form data
    // This prevents passing unvalidated data to the parent

    return (
      <div
        className={`${styles.form_section} ${isOpen ? "" : styles.close} ${
          styles.data_bounds
        }`}
      >
        <div
          className={styles.section_header}
          onClick={() => toggleSection("sec4")}
        >
          <div className={styles.section_number}>3</div>
          <div>
            <p className={styles.section_heading}>Data bounds</p>
            <p className={styles.sub_heading}>Patient Thresholds </p>
          </div>
          <div className={styles.drop_icon}>
            <FaChevronDown />
          </div>
        </div>
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className={styles.input_fields}
        >
          <div className={styles.input_status}>
            <p className={styles.high}>Low</p>
            <p className={styles.low}>High</p>
          </div>

          <div className={styles.input_group}>
            {/* Glucose */}
            <label>Glucose</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="text"
                  placeholder="70"
                  {...register("glucose_low")}
                  className={`${styles.input} ${
                    errors.glucose_low ? styles.error : ""
                  }`}
                />
                <input
                  type="text"
                  placeholder="130"
                  {...register("glucose_high")}
                  className={`${styles.input} ${
                    errors.glucose_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.glucose_low && (
                  <p className="error-message">{errors.glucose_low.message}</p>
                )}
                {errors.glucose_high && (
                  <p className="error-message">{errors.glucose_high.message}</p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Systolic */}
            <label>Systolic</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="95"
                  {...register("systolic_low")}
                  className={`${styles.input} ${
                    errors.systolic_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="170"
                  {...register("systolic_high")}
                  className={`${styles.input} ${
                    errors.systolic_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.systolic_low && (
                  <p className="error-message">{errors.systolic_low.message}</p>
                )}
                {errors.systolic_high && (
                  <p className="error-message">
                    {errors.systolic_high.message}
                  </p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Diastolic */}
            <label>Diastolic</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="45"
                  {...register("diastolic_low")}
                  className={`${styles.input} ${
                    errors.diastolic_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="105"
                  {...register("diastolic_high")}
                  className={`${styles.input} ${
                    errors.diastolic_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.diastolic_low && (
                  <p className="error-message">
                    {errors.diastolic_low.message}
                  </p>
                )}
                {errors.diastolic_high && (
                  <p className="error-message">
                    {errors.diastolic_high.message}
                  </p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Heart Rate */}
            <label>Heart Rate</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="50"
                  {...register("heart_rate_low")}
                  className={`${styles.input} ${
                    errors.heart_rate_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="100"
                  {...register("heart_rate_high")}
                  className={`${styles.input} ${
                    errors.heart_rate_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.heart_rate_low && (
                  <p className="error-message">
                    {errors.heart_rate_low.message}
                  </p>
                )}
                {errors.heart_rate_high && (
                  <p className="error-message">
                    {errors.heart_rate_high.message}
                  </p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Weight */}
            <label>Weight</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="140"
                  {...register("weight_low")}
                  className={`${styles.input} ${
                    errors.weight_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="220"
                  {...register("weight_high")}
                  className={`${styles.input} ${
                    errors.weight_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.weight_low && (
                  <p className="error-message">{errors.weight_low.message}</p>
                )}
                {errors.weight_high && (
                  <p className="error-message">{errors.weight_high.message}</p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Oxygen */}
            <label>Oxygen</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="90"
                  {...register("oxygen_low")}
                  className={`${styles.input} ${
                    errors.oxygen_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="100"
                  {...register("oxygen_high")}
                  className={`${styles.input} ${
                    errors.oxygen_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.oxygen_low && (
                  <p className="error-message">{errors.oxygen_low.message}</p>
                )}
                {errors.oxygen_high && (
                  <p className="error-message">{errors.oxygen_high.message}</p>
                )}
              </section>
            </div>
          </div>

          <div className={styles.input_group}>
            {/* Temperature */}
            <label>Temperature</label>
            <div className={styles.input_wrapper}>
              <section className={styles.input_section}>
                <input
                  type="number"
                  placeholder="96"
                  {...register("temperature_low")}
                  className={`${styles.input} ${
                    errors.temperature_low ? styles.error : ""
                  }`}
                />
                <input
                  type="number"
                  placeholder="100"
                  {...register("temperature_high")}
                  className={`${styles.input} ${
                    errors.temperature_high ? styles.error : ""
                  }`}
                />
              </section>
              <section className={styles.error_section}>
                {errors.temperature_low && (
                  <p className="error-message">
                    {errors.temperature_low.message}
                  </p>
                )}
                {errors.temperature_high && (
                  <p className="error-message">
                    {errors.temperature_high.message}
                  </p>
                )}
              </section>
            </div>
          </div>

          {/* <div className={styles.input_group}>
          <Button
            type="submit"
            className={styles.save_button}
            onLoading={isSaving}
            onSuccess={isSuccess}
          />
        </div> */}
        </form>
      </div>
    );
  }
);

export default DataBounds;
